import React, {useEffect, useRef} from 'react';
import './ArrBanner.scss';
import {Box, Typography} from '@mui/material';
import ArrButton from '../arr-button/ArrButton';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {GAToastTrackingInfo} from '../../../types/analyticsTypes';

type ArrBannerVariant = 'success' | 'warning' | 'error' | 'info' | 'complete';
type ArrBannerType = 'persistent' | 'contextual';

interface ArrBannerAction {
  label: string;
  onClick: () => void;
  startIcon?: React.ReactNode;
  dataTestId: string;
}

interface IBannerProps {
  type: ArrBannerType;
  variant: ArrBannerVariant;
  startIcon?: React.ReactNode;
  bannerContent: string | React.ReactNode;
  actions?: ArrBannerAction[];
  targetElementId?: string;
  className?: string;
  bannerContentClass?: string;
  dataTestId: string;
  gaTrackingInfo?: GAToastTrackingInfo;
}

const variantStyles: Record<
  ArrBannerVariant,
  {className: string; role: string}
> = {
  success: {
    className: 'arr-banner--success',
    role: 'status',
  },
  warning: {
    className: 'arr-banner--warning',
    role: 'alert',
  },
  error: {
    className: 'arr-banner--error',
    role: 'alert',
  },
  info: {
    className: 'arr-banner--info',
    role: 'status',
  },
  complete: {
    className: 'arr-banner--complete',
    role: 'status',
  },
};

const ArrBanner: React.FC<IBannerProps> = ({
  type,
  variant,
  startIcon,
  bannerContent,
  actions = [],
  targetElementId,
  className = '',
  bannerContentClass = 'body3_size',
  dataTestId,
  gaTrackingInfo,
}) => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const lastTrackedTextRef = useRef('');
  const {trackUserError} = useAnalytics();

  useEffect(() => {
    if (gaTrackingInfo?.dontTrack) {
      return;
    }

    const isTrackableVariant =
      variant === 'error' ||
      (variant === 'warning' && gaTrackingInfo != null);

    if (!isTrackableVariant) {
      return;
    }

    const text =
      typeof bannerContent === 'string' ? bannerContent.trim() : '';

    if (!text || text === lastTrackedTextRef.current) {
      return;
    }

    lastTrackedTextRef.current = text;

    trackUserError({
      text,
      ...(gaTrackingInfo?.code && {code: gaTrackingInfo.code}),
      ...(gaTrackingInfo?.type && {type: gaTrackingInfo.type}),
    });
  }, [variant, bannerContent, gaTrackingInfo, trackUserError]);

  // Contextual positioning logic
  useEffect(() => {
    if (type === 'contextual' && targetElementId && bannerRef.current) {
      const target = document.getElementById(targetElementId);
      if (target) {
        const targetRect = target.getBoundingClientRect();
        const scrollTop = window.scrollY || document.documentElement.scrollTop;

        // Convert px values to rem
        const baseFontSize = 16;
        const top = (targetRect.bottom + scrollTop + 8) / baseFontSize;
        const left = targetRect.left / baseFontSize;
        const width = targetRect.width / baseFontSize;

        const el = bannerRef.current;
        el.style.position = 'absolute';
        el.style.top = `${top}rem`;
        el.style.left = `${left}rem`;
        el.style.width = `${width}rem`;
        el.style.zIndex = '1000';
      }
    }
  }, [type, targetElementId]);

  const {className: variantClass, role} = variantStyles[variant];

  return (
    <Box
      ref={bannerRef}
      role={role as 'status' | 'alert'}
      aria-live={role === 'alert' ? 'assertive' : 'polite'}
      className={`arr-banner ${variantClass} ${type} ${className}`}
      data-testid={dataTestId}
    >
      <Box className="arr-banner__content">
        {startIcon && <Box>{startIcon}</Box>}
        <Typography
          className={`typo-text ${bannerContentClass}`}
          data-testid={`${dataTestId}-banner-content`}
          tabIndex={0}
        >
          {bannerContent}
        </Typography>
      </Box>
      <Box sx={{display: 'flex', alignItems: 'center'}}>
        {actions.slice(0, 3).map((action, index) => (
          <ArrButton
            variant="text"
            key={action.label}
            size="small"
            onClick={action.onClick}
            className="arr-banner__action "
            dataTestId={`arr-banner-button-${action.dataTestId}`}
          >
            {action.startIcon && (
              <span data-testid={`arr-banner-span-start-icon-${index}`}>
                {action.startIcon}
              </span>
            )}
            {action.label}
          </ArrButton>
        ))}
      </Box>
    </Box>
  );
};
export default ArrBanner;
